import { APIResponseType } from "../../types/generics/APIResponse.type";
import {
  createDeleteApiHook,
  createGetApiHook,
  createPostApiHook,
  createPatchApiHook,
} from "./apiHooks";
import { InstantCardData } from "../../components/InstantCard/types";

const createInstantCard = `/instant-cards`;

type cardQueryParams = Record<string, any>;

// AI Twin object for instant card payload
export interface AITwinPayload {
  talent_id: number; // Talent ID of the AI Twin
  validity_peroid: string; // Validity period (note: API uses "peroid" typo)
  start_date: string; // Start date in ISO 8601 format
  end_date: string; // End date in ISO 8601 format
}

// Payload type for creating/updating instant cards with AI Twin and license support
export interface CreateInstantCardPayload {
  suit: "Heart" | "Spade";
  duration: number; // 8 or 15
  chip: number;
  thumbnail: string;
  title?: string;
  description: string;
  mode: string;
  availability?: boolean;
  is_private_hall?: boolean;
  ait?: AITwinPayload; // Optional AI Twin object with license information
}

const getAllInstantCardAPI = (queryParams: cardQueryParams = {}): string => {
  const fullParams = {
    ...queryParams,
    offset: queryParams.offset ?? 0,
    limit: queryParams.limit ?? 1000,
  };

  const queryString = new URLSearchParams(fullParams).toString();
  return `/instant-cards?${queryString}`;
};

const getInstantCardById = ({ id }: { id: number }) => `/instant-cards/${id}`;

/**
 * API Hook for creating instant cards with AI Twin and license support
 * Supports creating cards with optional AI Twin integration and license information
 */
export const useCreateInstantCard = createPostApiHook<
  CreateInstantCardPayload,
  { headers: Record<any, any> },
  APIResponseType<InstantCardData>
>(createInstantCard, {
  isFormDataMethod: false,
});

export const useGetAllInstantCards = createGetApiHook<
  APIResponseType<InstantCardData[]>,
  {}
>(getAllInstantCardAPI);

export const useGetInstantCardByID = createGetApiHook<
  APIResponseType<InstantCardData>,
  { id: number }
>(getInstantCardById);

const getAllInstantCardByTalentAPI = (
  queryParams: cardQueryParams = {}
): string => {
  const fullParams = {
    ...queryParams,
    offset: queryParams.offset ?? 0,
    limit: queryParams.limit ?? 1000,
  };

  const queryString = new URLSearchParams(fullParams).toString();
  return `/instant-cards/me?${queryString}`;
};

export const useGetAllInstantCardByTalent = createGetApiHook<
  APIResponseType<InstantCardData[]>,
  {}
>(getAllInstantCardByTalentAPI);

/**
 * API Hook for updating instant cards with AI Twin and license support
 * Supports updating cards with optional AI Twin integration and license information
 */
export const useEditInstantCard = createPatchApiHook<
  Partial<CreateInstantCardPayload>,
  { id: number },
  APIResponseType<InstantCardData>
>(getInstantCardById, {
  isFormDataMethod: false,
});

export const useDeleteInstantCard = createDeleteApiHook<
  {},
  { id: number },
  APIResponseType<any>
>(getInstantCardById, {
  isFormDataMethod: false,
});

const getAllInstantCardBookingRequests = (
  queryParams: cardQueryParams = {}
): string => {
  const fullParams = {
    ...queryParams,
    offset: queryParams.offset ?? 0,
    limit: queryParams.limit ?? 1000,
  };

  const queryString = new URLSearchParams(fullParams).toString();
  return `/instant-cards/requests?${queryString}`;
};

export const useGetInstantCardRequests = createGetApiHook<
  APIResponseType<any>,
  {}
>(getAllInstantCardBookingRequests);

const bookInstantCard = `/instant-cards/request`;

export const useBookInstantCard = createPostApiHook<
  {},
  { instant_card_id: number },
  APIResponseType<any>
>(bookInstantCard, {
  isFormDataMethod: false,
});

export const respondBooking = `/instant-cards/response`;

export const useRespondRequest = createPostApiHook<
  {},
  { headers?: Record<any, any> },
  APIResponseType<any>
>(respondBooking, {
  isFormDataMethod: false,
});

const startInstantCardBooking = ({ id }: { id: number }) =>
  `/instant-cards/start/${id}`;

export const useStartInstantCardBooking = createPostApiHook<
  {},
  { headers: Record<any, any>; id: number },
  APIResponseType<any>
>(startInstantCardBooking, {
  isFormDataMethod: false,
});

const endInstantCardBooking = ({ id }: { id: number }) =>
  `/instant-cards/end/${id}`;

export const useEndInstantCardBooking = createPostApiHook<
  {},
  { headers: Record<any, any>; id: number },
  APIResponseType<any>
>(endInstantCardBooking, {
  isFormDataMethod: false,
});

const joinInstantCardBooking = ({ id }: { id: number }) =>
  `/instant-cards/join/${id}`;

export const useJoinInstantCardBooking = createPostApiHook<
  {},
  { id: number },
  APIResponseType<any>
>(joinInstantCardBooking, {
  isFormDataMethod: false,
});

const addReviews = ({ id }: { id: number }) => `/instant-cards/${id}/reviews`;

export const useAddReviews = createPostApiHook<
  {},
  { id: number },
  APIResponseType<any>
>(addReviews, {
  isFormDataMethod: false,
});

const giveTips = ({ id }: { id: number }) => `/instant-cards/${id}/tip`;

export const useGiveTips = createPostApiHook<
  {},
  { id: number },
  APIResponseType<any>
>(giveTips, {
  isFormDataMethod: false,
});

type CardQueryParamswithId = Record<string, any> & { id: number };

const getInstantCardReviews = ({
  id,
  ...queryParams
}: CardQueryParamswithId): string => {
  const fullParams = {
    ...queryParams,
    offset: queryParams.offset ?? 0,
    limit: queryParams.limit ?? 1000,
  };

  const queryString = new URLSearchParams(fullParams).toString();
  return `/instant-cards/${id}/reviews?${queryString}`;
};

export const useGetInstantCardReviews = createGetApiHook<
  APIResponseType<any>,
  { id: number } & CardQueryParamswithId
>(getInstantCardReviews);

const getInstantCardTips = ({ id }: { id: number }) =>
  `/instant-cards/${id}/tip`;

export const useGetInstantCardTips = createGetApiHook<
  APIResponseType<any>,
  { id: number }
>(getInstantCardTips);

const startInstantCardBookingBusiness = ({
  id,
  business_id,
}: {
  id: number;
  business_id: number;
}) => `/instant-cards/start/${id}?business_id=${business_id}`;

export const useStartInstantCardBookingBusiness = createPostApiHook<
  {},
  { id: number; business_id: number },
  APIResponseType<any>
>(startInstantCardBookingBusiness, {
  isFormDataMethod: false,
});

const endInstantCardBookingBusiness = ({
  id,
  business_id,
}: {
  id: number;
  business_id: number;
}) => `/instant-cards/end/${id}?business_id=${business_id}`;

export const useEndInstantCardBookingBusiness = createPostApiHook<
  {},
  { id: number; business_id: number },
  APIResponseType<any>
>(endInstantCardBookingBusiness, {
  isFormDataMethod: false,
});

// API for creating/updating thread_id for instant card sessions
const createOrUpdateSessionThread = ({ id }: { id: number }) =>
  `/instant-cards/sessions/${id}/thread`;

export const useCreateOrUpdateSessionThread = createPostApiHook<
  { thread_id: string },
  { id: number },
  APIResponseType<any>
>(createOrUpdateSessionThread, {
  isFormDataMethod: false,
});

const getInstantCardBookingById = ({ id }: { id: number }) =>
  `/instant-cards/requests/${id}`;

export const useGetInstantCardBookingById = createGetApiHook<
  APIResponseType<any>,
  { id: number }
>(getInstantCardBookingById);
